import React, { useEffect, useState } from "react";
import {
    Alert, Autocomplete, Box, Button, CircularProgress, FormControlLabel, Grid, MenuItem, Paper, Stack, Switch, TextField, Typography,
} from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import reportServices from "app/services/automations/report-services";
import reportTemplateServices from "app/services/automations/report-template-services";
import { requestErrors } from "app/services/automations/generated-file-services";
import { SaveButton, StandardPageHeader } from "app/shared/ui";
import { getReportType } from "../types";
import useReportCatalog from "../useReportCatalog";
import ReportTemplatePreview from "./ReportTemplatePreview";

const LIST_PATH = "/automations/reports";
const ASK_STORE = { id: "", name: "Elegir al generar" };

// Crea o edita una plantilla de reporte: todo lo del reporte menos las fechas, que se eligen al generarla. El local
// puede quedar fijo o elegirse cada vez; sin local fijo van todos sus equipos. Debajo, la vista previa se actualiza
// sola con cada cambio.
const ReportTemplateEditor = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const catalog = useReportCatalog();
    const backTo = location.state?.from || LIST_PATH;
    const draft = location.state?.draft || {};
    const [template, setTemplate] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [options, setOptions] = useState(null);
    const [loadingOptions, setLoadingOptions] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    // La plantilla a editar, o una nueva desde el borrador (reporte rápido, duplicar o «Nueva plantilla»).
    useEffect(() => {
        if (id) {
            reportTemplateServices.get(id)
                .then((data) => setTemplate({ ...data, store_id: data.store?.id || "" }))
                .catch(() => setLoadError("No se encontró la plantilla."));
            return;
        }
        if (catalog.loading) return;
        const reportType = draft.report_type || catalog.types[0]?.key;
        setTemplate({
            name: draft.name || "", report_type: reportType, store_id: draft.store_id ?? "", default_period: draft.default_period || "yesterday",
            shared: !!draft.shared, mine: true, can_edit: true,
            params: { ...(getReportType(reportType)?.defaultParams || {}), ...(draft.params || {}) },
        });
    }, [id, catalog.loading]); // eslint-disable-line react-hooks/exhaustive-deps

    const definition = template && getReportType(template.report_type);

    // Opciones del local fijo, o de la empresa si el local se elige al generar.
    useEffect(() => {
        if (!template?.report_type) return;
        setLoadingOptions(true);
        reportServices.getOptions(template.report_type, template.store_id || null)
            .then((data) => {
                setOptions(data);
                setTemplate((current) => ({ ...current, params: definition?.prepareParams?.(data, current.params) || current.params }));
            })
            .catch(() => setOptions(null))
            .finally(() => setLoadingOptions(false));
    }, [template?.report_type, template?.store_id]); // eslint-disable-line react-hooks/exhaustive-deps

    const update = (patch) => setTemplate((current) => ({ ...current, ...patch }));
    const updateParams = (patch) => setTemplate((current) => ({ ...current, params: { ...current.params, ...patch } }));

    const save = async () => {
        setSaving(true);
        setError(null);
        const body = {
            name: template.name.trim(), report_type: template.report_type, store_id: template.store_id || null,
            default_period: template.default_period, params: template.store_id ? template.params : { ...template.params, device_ids: [] },
            ...(template.mine ? { shared: template.shared } : {}),
        };
        try {
            const saved = id ? await reportTemplateServices.update(id, body) : await reportTemplateServices.create(body);
            navigate(backTo.startsWith(LIST_PATH) ? LIST_PATH : backTo, { state: { notice: `Plantilla «${saved.name}» guardada.` } });
        } catch (requestError) {
            setError(requestErrors(requestError, "No se pudo guardar la plantilla."));
            setSaving(false);
        }
    };

    if (loadError) return <Alert severity="error">{loadError}</Alert>;
    if (!template || catalog.loading) return <Box sx={{ py: 6, textAlign: "center" }}><CircularProgress /></Box>;
    if (!definition) return <Alert severity="warning">Este reporte no está disponible en esta versión de la aplicación.</Alert>;

    const storeOptions = [ASK_STORE, ...catalog.stores];
    const typeTitle = catalog.types.find((type) => type.key === template.report_type)?.title;
    const canSave = !saving && !loadingOptions && !!template.name.trim() && definition.canBuild(options, template.params);

    return (
        <Box>
            <StandardPageHeader title={id ? "Editar plantilla" : "Nueva plantilla"} subtitle={typeTitle} backTo={backTo} backLabel="Volver a Reportes" />
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={4}>
                        <TextField fullWidth label="Nombre" value={template.name} placeholder="Ej: Planilla diaria de bodega"
                                   onChange={(event) => update({ name: event.target.value })} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Autocomplete
                            options={storeOptions} disableClearable
                            value={storeOptions.find((store) => `${store.id}` === `${template.store_id}`) || ASK_STORE}
                            getOptionLabel={(option) => option?.name || ""}
                            isOptionEqualToValue={(option, value) => `${option.id}` === `${value.id}`}
                            onChange={(event, value) => setTemplate((current) => ({ ...current, store_id: value?.id || "", params: { ...current.params, device_ids: [] } }))}
                            renderInput={(inputParams) => (
                                <TextField {...inputParams} label="Local"
                                           helperText={template.store_id ? null : "Se elige cada vez que se genera; van todos sus equipos."} />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField select fullWidth label="Período sugerido" value={template.default_period}
                                   helperText="Se propone al generarla; siempre se puede cambiar."
                                   onChange={(event) => update({ default_period: event.target.value })}>
                            {catalog.periods.map((period) => <MenuItem key={period.value} value={period.value}>{period.label}</MenuItem>)}
                        </TextField>
                    </Grid>
                </Grid>

                <definition.Fields options={options} value={template.params} onChange={updateParams} showDevices={!!template.store_id} />

                <Box sx={{ mt: 2 }}>
                    {template.mine ? (
                        <FormControlLabel
                            control={<Switch checked={!!template.shared} onChange={(event) => update({ shared: event.target.checked })} />}
                            label={(
                                <Box>
                                    <Typography variant="body2">Compartir con la empresa</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        La verán todos los que usan reportes. Solo tú y los administradores pueden editarla.
                                    </Typography>
                                </Box>
                            )}
                        />
                    ) : (
                        <Typography variant="caption" color="text.secondary">
                            Plantilla compartida por {template.owner?.name}. Solo quien la creó decide si se comparte.
                        </Typography>
                    )}
                </Box>

                {!!error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

                <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
                    <Button onClick={() => navigate(backTo)}>Cancelar</Button>
                    <SaveButton loading={saving} disabled={!canSave} onClick={save}>Guardar plantilla</SaveButton>
                </Stack>
            </Paper>

            <ReportTemplatePreview
                template={template} definition={definition} stores={catalog.stores} periods={catalog.periods}
                ready={!loadingOptions && definition.canBuild(options, template.params)} earliestDate={options?.earliest_date}
            />
        </Box>
    );
};

export default ReportTemplateEditor;
